import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { HandleRoomChangeProps, RoomSelection } from './types';

export const HotelSearchRoomInputWidgetItem = ({
  room,
  roomIndex,
  onRoomChange,
  onRemove,
  canRemove,
  autoFocus,
}: {
  room: RoomSelection;
  roomIndex: number;
  onRoomChange: (props: HandleRoomChangeProps) => void;
  onRemove: () => void;
  canRemove: boolean;
  autoFocus?: boolean;
}) => {
  const { t } = useTranslation();
  const id = useId();
  const [adults, setAdults] = useState(() => room.paxes.filter((age) => age >= 18).map(String));
  const [children, setChildren] = useState(() => room.paxes.filter((age) => age < 18).map(String));
  const [adultCount, setAdultCount] = useState(String(adults.length));
  const [childCount, setChildCount] = useState(String(children.length));
  const [showAdults, setShowAdults] = useState(false);
  const publish = (a: string[], c: string[]) =>
    onRoomChange({ roomIndex, paxes: [...a, ...c].map((age) => (age === '' ? NaN : Number(age))) });
  const countChange = (value: string, adult: boolean) => {
    (adult ? setAdultCount : setChildCount)(value);
    const count = Number(value),
      current = adult ? adults : children;
    // Keep each valid count and its ages in sync even while the combined draft
    // exceeds four travelers. Native limits and dialog validation block Apply
    // until the advisor finishes redistributing the other count.
    if (value === '' || !Number.isInteger(count) || count < (adult ? 1 : 0) || count > (adult ? 4 : 3)) return;
    const next = Array.from({ length: count }, (_, i) => current[i] ?? (adult ? '18' : ''));
    (adult ? setAdults : setChildren)(next);
    publish(adult ? next : adults, adult ? children : next);
  };
  const ages = (values: string[], adult: boolean) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 1.5, mt: 2 }}>
      {values.map((age, index) => (
        <TextField
          key={index}
          id={`${id}-${adult ? 'a' : 'c'}-${index}`}
          label={t(adult ? 'hotels.roomMenu.adultAge' : 'hotels.roomMenu.minorAge', { index: index + 1 })}
          value={age}
          required
          type="number"
          size="small"
          inputProps={{ min: adult ? 18 : 0, max: adult ? 100 : 17, step: 1, inputMode: 'numeric' }}
          helperText={!adult && index === 0 ? t('hotels.roomMenu.infantAge') : undefined}
          onChange={(event) => {
            const next = values.map((value, i) => (i === index ? event.target.value : value));
            (adult ? setAdults : setChildren)(next);
            publish(adult ? next : adults, adult ? children : next);
          }}
        />
      ))}
    </Box>
  );
  return (
    <Box component="fieldset" sx={{ m: 0, minWidth: 0, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Typography component="legend" variant="subtitle2" sx={{ px: 0.75 }}>
        {t('hotels.roomMenu.room')} {roomIndex + 1}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label={t('hotels.roomMenu.adults')}
          value={adultCount}
          onChange={(event) => countChange(event.target.value, true)}
          autoFocus={autoFocus}
          required
          type="number"
          size="small"
          helperText={t('hotels.roomMenu.adultRange')}
          inputProps={{ min: 1, max: 4 - children.length, step: 1, inputMode: 'numeric' }}
        />
        <TextField
          label={t('hotels.roomMenu.minors')}
          value={childCount}
          onChange={(event) => countChange(event.target.value, false)}
          required
          type="number"
          size="small"
          helperText={t('hotels.roomMenu.childRange')}
          inputProps={{ min: 0, max: 4 - adults.length, step: 1, inputMode: 'numeric' }}
        />
      </Box>
      {children.length > 0 && ages(children, false)}
      <Box
        component="details"
        open={showAdults}
        onInvalidCapture={() => setShowAdults(true)}
        onToggle={(event) => setShowAdults(event.currentTarget.open)}
        sx={{ mt: 1.5 }}
      >
        <Typography component="summary" variant="body2" color="text.secondary" sx={{ cursor: 'pointer' }}>
          {t('hotels.roomMenu.adultAges')} · {adults.join(', ')}
        </Typography>
        {ages(adults, true)}
      </Box>
      {canRemove && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button type="button" size="small" onClick={onRemove}>
            {t('hotels.roomMenu.removeRoom')}
          </Button>
        </Stack>
      )}
    </Box>
  );
};
